import { makeAutoObservable, runInAction } from "mobx";
import { ProjectApi } from "../../project/services/ProjectApi";
import { DashboardTypes } from "../models/Dashboard";
import { RootStore } from "../../shared/stores/RootStore";
import { DashboardApi } from "../services/DashboardApi";

const defaultDashboardItems: DashboardTypes.Item[] = [
  DashboardTypes.getDefaultItemByType(DashboardTypes.ItemType.MiniMap),
  DashboardTypes.getDefaultItemByType(
    DashboardTypes.ItemType.PieChartFullScore,
  ),
  DashboardTypes.getDefaultItemByType(DashboardTypes.ItemType.PieChartCraColoring),
  DashboardTypes.getDefaultItemByType(
    DashboardTypes.ItemType.TableTreatmentSuggestionCounter,
  ),
  DashboardTypes.getDefaultItemByType(
    DashboardTypes.ItemType.TableDistancePerAttributeValue,
  ),
];

export class DashboardStore {
  rootStore;
  dashboards: DashboardTypes.Dashboard[] = [];
  selectedDashboard?: DashboardTypes.Dashboard;
  dashboardItems = defaultDashboardItems;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.dashboards = [];
      this.selectedDashboard = undefined;
      this.dashboardItems = defaultDashboardItems;
    });
  }

  setSelectedDashboard(dashboardId: string | undefined) {
    runInAction(() => {
      const dashboard = dashboardId
        ? this.dashboards?.find((dashboard) => dashboard.id === dashboardId)
        : undefined;
      if (dashboard) {
        this.selectedDashboard = dashboard;
        this.dashboardItems = dashboard.items;
      } else {
        this.selectedDashboard = undefined;
        this.dashboardItems = defaultDashboardItems;
      }
    });
  }

  async createDashboard(request: DashboardTypes.CreateDashboardRequest) {
    const response = await DashboardApi.create(request);
    runInAction(() => {
      this.dashboards = [...this.dashboards, response.data];
      this.selectedDashboard = response.data;
    });
    return response;
  }

  async updateDashboard(
    id: string,
    request: DashboardTypes.UpdateDashboardRequest,
  ) {
    const response = await DashboardApi.update(id, request);
    runInAction(() => {
      this.dashboards = this.dashboards.map((item) =>
        item.id === id ? response.data : item,
      );
      this.selectedDashboard = response.data;
    });
    return response;
  }

  async deleteDashboard(id: string) {
    await DashboardApi.delete(id);
    runInAction(() => {
      this.dashboards = this.dashboards.filter((item) => item.id !== id);
      this.selectedDashboard = undefined;
    });
  }

  async fetchDashboards(projectId: string, cancelIf?: () => boolean) {
    const response = await ProjectApi.listDashboards(projectId);
    if (cancelIf?.()) return;
    runInAction(() => {
      this.dashboards = response.data;
    });
  }

  addDashboardItem(item: DashboardTypes.Item) {
    runInAction(() => {
      this.dashboardItems = [...this.dashboardItems, item];
    });
  }

  updateDashboardItem(index: number, item: DashboardTypes.Item) {
    runInAction(() => {
      this.dashboardItems = this.dashboardItems.map((prevItem, i) =>
        i === index ? item : prevItem,
      );
    });
  }

  deleteDashboardItem(index: number) {
    runInAction(() => {
      this.dashboardItems = this.dashboardItems.filter((_, i) => i !== index);
    });
  }

  reorderDashboardItems(reorderedItems: DashboardTypes.Item[]) {
    runInAction(() => {
      this.dashboardItems = reorderedItems;
    });
  }
}
